/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash/fp';
import styled from '@emotion/styled';
import { margin, padding } from 'polished';
import { compose } from 'recompose';
import Tappable from 'react-tappable';

import EmailInviteForm from 'app/components/commons/EmailInviteForm';
import Modal from 'app/components/commons/Modal';
import { rewritePath } from 'app/utils/routeUtils';
import NameUpdateForm from 'app/components/user/NameUpdateForm';
import ConnectedSocialShareButtons from 'app/components/commons/ConnectedSocialShareButtons';
import { updateUserRealName } from 'app/actions/userActions';
import { resetChallenge } from 'app/actions/challengeActions';
import { hover } from 'app/utils/cssUtils';
import t from 'app/utils/translate';
import { noOrphanWords } from 'app/utils/stringUtils';
import Button from 'app/components/commons/Button';
import AdImageLink from 'app/components/commons/AdImageLink';

type Props = {
  canEnterAgain?: boolean,
  challengeId?: string,
  children: any,
  displayFacebook?: boolean,
  displayTwitter?: boolean,
  displayEmail?: boolean,
  history: Object,
  lineupId?: number,
  isUserUpdating: boolean,
  onClose: () => void,
  onUserUpdate: () => void,
  onReset: () => void,
  rewriteRules?: Object,
  showNameForm: boolean,
  submittedChallengeEntryId?: number,
  submitMore?: boolean,
  userErrors: Array<*>,
  challengeEnteredAd?: Object
};

const Banner = styled.div(({ theme }) => ({
  color: theme.color.basis2,
  ...padding('1rem', '1.5rem')
}));

const CancelButton = styled(props => <button type="button" {...props} />)(({ theme }) => ({
  display: 'block',
  fontSize: '1.125rem',
  textTransform: 'uppercase',
  ...margin('0.5rem', 'auto', '0'),
  ...padding('0.25rem'),
  ...hover({
    color: theme.color.basis5
  })
}));

const Checkmark = styled(({ className }) => (
  <span className={`ion-checkmark-circled ${className}`} />
))(({ theme }) => ({
  color: theme.color.success,
  fontSize: '3rem'
}));

const Detail = styled.div({
  ...padding('0.25rem', '0'),
  textAlign: 'center'
});

const InviteFormWrapper = styled.div(({ theme }) => ({
  color: theme.color.basis2,
  ...padding('0', null, '1rem')
}));

const ModalContent = styled.div(
  {
    borderRadius: 6,
    position: 'relative',
    textAlign: 'center'
  },
  ({ theme }) => ({
    backgroundColor: theme.color.white
  })
);

const NameFormWrapper = styled.div(({ theme }) => ({
  backgroundColor: 'grey',
  color: theme.color.basis2,
  ...padding('0.5rem', '1rem')
}));

const ShareWrapper = styled.div(({ theme }) => ({
  ...padding('1rem', null, '0.5rem'),
  fontSize: theme.fontSize.large,
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: theme.fontSize.medium
  }
}));

const Top = styled.div(({ theme }) => ({
  color: theme.color.basis1,
  fontSize: '1.625rem',
  textTransform: 'uppercase'
}));

const StyledHr = styled(({ className }) => <hr className={`hr__dark ${className}`} />)({
  width: '80%'
});

const ButtonContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center'
});

const StyledButton = styled(Button)(({ theme }) => ({
  minWidth: '6rem',
  margin: theme.spacing.xsmall,
  marginBottom: theme.spacing.large,
  [theme.mediaQuery.mobileSmallOnly]: {
    minWidth: '5rem'
  }
}));

const AdButtonImage = styled.img(({ theme }) => ({
  height: 'auto',
  width: '50px',
  [theme.mediaQuery.mobileSmallOnly]: {
    width: '40px'
  }
}));

class ChallengeEnteredModal extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    canEnterAgain: false,
    challengeId: undefined,
    displayFacebook: false,
    displayTwitter: false,
    displayEmail: false,
    lineupId: undefined,
    rewriteRules: {},
    submittedChallengeEntryId: null,
    submitMore: false,
    challengeEnteredAd: undefined
  };

  componentDidUpdate(prevProps: Props) {
    const { userErrors, isUserUpdating, onClose } = this.props;

    if (prevProps.isUserUpdating && !isUserUpdating && userErrors.length === 0) {
      onClose();
    }
  }

  handleEnterAgain = () => {
    const { canEnterAgain, onClose, onReset } = this.props;
    if (canEnterAgain) {
      onReset();
      onClose();
    }
  };

  handleCloseModal = () => {
    const { challengeId, history, submittedChallengeEntryId, rewriteRules, onClose } = this.props;
    const rewrite = rewritePath(rewriteRules);

    onClose();

    if (challengeId && submittedChallengeEntryId) {
      history.push(rewrite(`/challenge/${challengeId}/${submittedChallengeEntryId}`));
    }
  };

  render() {
    const {
      canEnterAgain,
      children,
      displayFacebook,
      displayTwitter,
      displayEmail,
      lineupId,
      isUserUpdating,
      onClose,
      onUserUpdate,
      showNameForm,
      submitMore,
      challengeEnteredAd,
      userErrors
    } = this.props;

    return (
      <Modal bare isOpen onCancel={this.handleCloseModal} size="medium">
        <ModalContent>
          <Banner>
            <Top>
              <Checkmark />
            </Top>
            <Detail>{children}</Detail>
          </Banner>
          <StyledHr />
          <InviteFormWrapper>
            {challengeEnteredAd && challengeEnteredAd.enabled && (
              <AdImageLink ad={challengeEnteredAd} />
            )}
            {displayEmail && <EmailInviteForm />}
            <ShareWrapper>
              <ConnectedSocialShareButtons
                clipboard
                email={displayEmail}
                facebook={displayFacebook}
                onClose={onClose}
                twitter={displayTwitter}
                vibrant
              />
            </ShareWrapper>
          </InviteFormWrapper>
          {showNameForm && (
            <NameFormWrapper>
              <Detail>
                <div>
                  {noOrphanWords(t('Did you know you can search for your friends by name?'))}
                </div>
                <div>
                  {noOrphanWords(t('Add your name below so you can challenge each other.'))}
                </div>
              </Detail>
              <NameUpdateForm
                errors={userErrors}
                isUpdating={isUserUpdating}
                onSubmit={onUserUpdate}
              />
              <Tappable component={CancelButton} onTap={onClose}>
                {t('Continue')}
              </Tappable>
            </NameFormWrapper>
          )}
          <ButtonContainer>
            <StyledButton size="medium" kind="secondary" link="/lobby">
              {t('Lobby')}
            </StyledButton>
            {submitMore ? (
              <StyledButton
                size="medium"
                kind="primary"
                link={`/enter-additional-challenges/${lineupId}`}
              >
                {t('Join More')}
              </StyledButton>
            ) : (
              <StyledButton size="medium" kind="cancel" onClick={onClose}>
                {t('Close')}
              </StyledButton>
            )}
            {canEnterAgain && (
              <StyledButton size="medium" kind="primary" onClick={this.handleEnterAgain}>
                {t('Enter Again')}
              </StyledButton>
            )}
            {challengeEnteredAd && challengeEnteredAd.enabled && (
              <StyledButton
                size="medium"
                kind="success"
                href={challengeEnteredAd.desktopLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Get')} <AdButtonImage src={challengeEnteredAd.buttonImageSrc} />
              </StyledButton>
            )}
          </ButtonContainer>
        </ModalContent>
      </Modal>
    );
  }
}

function mapStateToProps(state, { showNameForm }) {
  const {
    settings,
    user: { currentUser = {}, updatingUser, errors },
    entry
  } = state;

  const enableChallengeSocialShare = !!get('social.enableChallengeSocialShare', settings);

  let canEnterAgain = false;

  if (get('challenge.id', state)) {
    const { entryCap, maxEntriesPerPerson, entryCount, userEntryCount } = state.challenge;

    if (
      (entryCap === 0 || entryCap > entryCount + 1) &&
      (maxEntriesPerPerson === 0 || maxEntriesPerPerson > userEntryCount + 1)
    ) {
      canEnterAgain = true;
    }
  }

  return {
    canEnterAgain,
    challengeEnteredAd: get('kayoAds.challengeEnteredAd', settings),
    challengeId: get('challenge.id', state),
    displayFacebook: enableChallengeSocialShare && get('social.displayFacebook', settings),
    displayTwitter: enableChallengeSocialShare && get('social.displayTwitter', settings),
    displayEmail: false,
    isUserUpdating: updatingUser,
    lineupId: entry.lineupId,
    showNameForm: !!showNameForm && !currentUser.first_name && !currentUser.last_name,
    submittedChallengeEntryId: get('challenge.submittedChallengeEntryId', state),
    userErrors: errors,
    rewriteRules: settings.rewriteRules
  };
}

const actions = {
  onUserUpdate: updateUserRealName,
  onReset: resetChallenge
};

export default compose(withRouter, connect(mapStateToProps, actions))(ChallengeEnteredModal);
